// dependencies
import PropTypes from 'prop-types'
import React from 'react'

// components
import { Button, BUTTON_THEME, BUTTON_SIZE, Form } from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import Header from '_components/header/index.jsx'

// libraries
import sendAnalytics from '_root/lib/sendAnalytics.js'

// services
import User from '../../lib/User'

// css
import './styles.scss'

class EntityEditForm extends React.Component {
  constructor(props) {
    super(props)
    this.ref = React.createRef()
    this.state = {
      deleteModalOpen: false,
    }
  }

  componentDidMount() {
    this.loadSchema()
    this.ref.current.focus()
    User.watch(this, () => {
      this.loadEntity()
    })
  }

  loadEntity = () => {
    let { id, entities } = this.props
    window._api.get(`/${entities}/${id}`, (error, item) => {
      if (error) {
        toast.error(error.message)
      }
      this.setState({ item })

      // event tracking
      sendAnalytics('read', entities, item)
    })
  }

  loadSchema = () => {
    let { entities } = this.props
    window._api.get(`/${entities}/schema`, (error, schema) => {
      if (error) {
        toast.error(error.message)
      }
      if (schema) {
        this.setState({ schema })
      }
    })
  }

  onChange = (value, name, formattedValue) => {
    let is_modified = true
    let { item } = this.state
    item[name] = formattedValue || value
    this.setState({ item, is_modified })
  }

  // Exit, the header's back arrow, Escape/cmd-E, and the redirects after save
  // and delete all land here. `entities` is the API resource, which is also the
  // route for most pages - but not all, so `exitTo` lets a page whose URL
  // differs say where to go.
  onExit = () => {
    let { entities, exitTo } = this.props
    window._history.push(exitTo || `/${entities}`)
  }

  onKeyDown = (e) => {
    if (e.metaKey && e.key === 's') {
      e.preventDefault()
      this.onSave()
    }
    if (e.key === 'Escape' || (e.metaKey && e.key === 'e')) {
      e.preventDefault()
      this.onExit()
    }
  }

  onDelete = (e) => {
    let { entities, id } = this.props

    window._api.delete(`/${entities}/${id}`, {}, (error) => {
      this.setState({ error })

      // A refused delete used to land in state and nowhere else, so the button
      // simply appeared to do nothing - the reason it was refused never reached
      // the person who needed it.
      if (error) {
        return toast.error(error.message || 'Could not delete this item')
      }

      toast('Delete successful!')
      this.onExit()
    })
  }

  convertToBool = (item) => {
    if (typeof item === 'object') {
      return item.value === 'true' ? true : false
    }
    return item?.toString() === 'true' ? true : false
  }

  onSave = (e, stay, onError) => {
    let { entities, id, handleError } = this.props
    let { item, schema } = this.state

    const booleanFields = Object.keys(schema).filter((key) => schema[key].type === 'boolean')
    const booleanItems = booleanFields.reduce(
      (obj, booleanItem) => ({ ...obj, [booleanItem]: this.convertToBool(item[booleanItem]) }),
      {}
    )

    const newItem = {
      ...item,
      ...booleanItems,
    }

    window._api.put(`/${entities}/${id}`, newItem, (error, item) => {
      this.setState({ error })
      if (error) {
        if (typeof handleError === 'function') {
          handleError(error)
        } else {
          if (onError) onError()
          toast.error(error.message)
        }
      } else {
        if (this.props.onSave) {
          this.props.onSave()
        }

        if (!stay) {
          toast('Update successful!')
          this.onExit()
        }
      }
    })

    // event tracking
    sendAnalytics('update', entities, item)
  }

  toggleModal = () => {
    this.setState({ deleteModalOpen: !this.state.deleteModalOpen })
  }

  render() {
    let {
      className,
      children,
      deletable,
      fields,
      resource,
      style,
      skipPermissionCheck,
    } = this.props
    let { is_modified, item, schema, deleteModalOpen } = this.state || {}

    // check if user has permission
    let disabled = skipPermissionCheck ? false : !User.hasPermission(item, resource)

    // apply "enum", "multi", and "description" from schema to "fields"
    if (fields && schema) {
      for (let field of fields) {
        if (schema[field.name]) {
          if (schema[field.name].collection) {
            field.entity = schema[field.name].collection
          }
          if (schema[field.name].enum) {
            field.options = schema[field.name].enum
          }
          if (schema[field.name].description) {
            field.description = schema[field.name].description
          }
          if (schema[field.name].disabled || disabled) {
            field.disabled = schema[field.name].disabled || disabled
          }
          if (schema[field.name].type) {
            field.type = schema[field.name].type

            // replace _ with - in entity name
            if (field.type === 'ref') {
              field.entity = field.entity.replace('_', '-')
            }

            if (field.type === 'boolean') {
              field.options = schema[field.name].enum.map((item) => item.toString())
            }
          }
          field.multi = schema[field.name].multi || false
        }
      }
    }

    const commonActions = [
      {
        children: 'Exit',
        onClick: this.onExit,
        theme: BUTTON_THEME.GHOST,
      },
      {
        children: 'Save',
        onClick: this.onSave,
        disabled: !is_modified || disabled,
      },
      {
        children: 'Delete',
        red: true,
        onClick: this.toggleModal,
        disabled: !deletable || disabled,
      },
    ]
    const actions = this.props.actions.length
      ? [
          {
            children: 'Exit',
            onClick: this.onExit,
            theme: BUTTON_THEME.GHOST,
          },
          ...this.props.actions,
          {
            children: 'Save',
            onClick: this.onSave,
            disabled: !is_modified || disabled,
          },
        ]
      : commonActions

    return (
      <div
        className={`st-entity-edit-form ${className}`}
        onKeyDown={this.onKeyDown}
        ref={this.ref}
        style={style}
        tabIndex={-1}
      >
        <If condition={item}>
          <Header actions={actions} back={this.onExit} title={item.name} />

          <If condition={fields}>
            <Form item={item} onChange={this.onChange} fields={fields} />
            {this.props.additionalRender?.(item, schema, this.onChange)}
          </If>

          <If condition={children && item && schema}>
            {children({
              disabled,
              is_modified,
              item,
              onChange: this.onChange,
              onExit: this.onExit,
              onSave: this.onSave,
              schema,
            })}
          </If>
        </If>

        <If condition={deleteModalOpen}>
          <Dialog onClose={this.toggleModal} open={deleteModalOpen}>
            <div>
              <p>
                Are you sure you want to delete <b>{item?.name}?</b>
              </p>

              <div className="st-entity-edit-form__right">
                <Button red size={BUTTON_SIZE.SMALLER} onClick={this.onDelete}>
                  Yes, delete
                </Button>
                <Button
                  size={BUTTON_SIZE.SMALLER}
                  theme={BUTTON_THEME.SECONDARY}
                  onClick={this.toggleModal}
                >
                  No, cancel
                </Button>
              </div>
            </div>
          </Dialog>
        </If>
      </div>
    )
  }
}

EntityEditForm.propTypes = {
  className: PropTypes.string,
  actions: PropTypes.array,
  children: PropTypes.func,
  deletable: PropTypes.bool,
  entities: PropTypes.string,
  exitTo: PropTypes.string,
  fields: PropTypes.array,
  id: PropTypes.any,
  name: PropTypes.string,
  onSave: PropTypes.func,
  resource: PropTypes.string,
  style: PropTypes.object,
  handleError: PropTypes.func,
  skipPermissionCheck: PropTypes.bool,
}

EntityEditForm.defaultProps = {
  className: '',
  actions: [],
  children: () => {},
  deletable: true,
  entities: '',
  exitTo: '',
  fields: [],
  id: null,
  name: '',
  onSave: () => {},
  resource: '',
  style: {},
  skipPermissionCheck: false,
}

export default EntityEditForm
